import { useCallback, useEffect, useRef, useState } from 'react'

export type AiMode = 'off' | 'overlay' | 'active'

export function useAiSearchToggle() {
  const [aiMode, setAiMode] = useState<AiMode>('off')
  const [aiPrompt, setAiPrompt] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const promptRef = useRef(aiPrompt)

  // Keep prompt ref in sync
  useEffect(() => {
    promptRef.current = aiPrompt
  }, [aiPrompt])

  // Close overlay but keep toggle active if there's text
  const closeOverlay = useCallback(() => {
    const hasText = promptRef.current.trim().length > 0
    setAiMode(hasText ? 'active' : 'off')
  }, [])

  const clearPrompt = useCallback(() => {
    setAiPrompt('')
  }, [])

  useEffect(() => {
    if (aiMode !== 'overlay') return

    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeOverlay()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeOverlay()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [aiMode, closeOverlay])

  return {
    aiMode,
    aiPrompt,
    setAiMode,
    setAiPrompt,
    clearPrompt,
    closeOverlay,
    rootRef,
  }
}
